<script setup lang="ts">
import { computed, nextTick, ref } from 'vue'

import type { SegmentedControlValue } from './types'

import type { SelectOption } from '@/shared/ui/select'
import type { UiSize } from '@/shared/ui/types'

const model = defineModel<SegmentedControlValue>({ required: true })

const props = withDefaults(
  defineProps<{
    options: SelectOption<SegmentedControlValue>[]
    label: string
    size?: UiSize
    /**
     * 'solid' fills the selected option instead of the default raised-card look, and drops the
     * track's own chrome. 'filled' keeps the track but fills the selected option in brand primary
     * and runs the labels light — the report view switch (Table / Card), whose segments also hold
     * a common minimum width so a short label cannot narrow its segment.
     */
    variant?: 'default' | 'solid' | 'filled'
  }>(),
  { size: 'md', variant: 'default' },
)

const buttons = ref<HTMLButtonElement[]>([])
const enabledIndexes = computed(() =>
  props.options.flatMap((option, index) => (option.disabled ? [] : [index])),
)
const selectedIndex = computed(() =>
  props.options.findIndex((option) => Object.is(option.value, model.value) && !option.disabled),
)

function select(option: SelectOption<SegmentedControlValue>): void {
  if (option.disabled || Object.is(option.value, model.value)) return
  model.value = option.value
}

function tabIndex(index: number): 0 | -1 {
  const fallback = enabledIndexes.value[0] ?? -1
  return index === (selectedIndex.value >= 0 ? selectedIndex.value : fallback) ? 0 : -1
}

async function focusOption(index: number): Promise<void> {
  const option = props.options[index]
  if (!option || option.disabled) return
  select(option)
  await nextTick()
  buttons.value[index]?.focus({ preventScroll: true })
}

async function move(currentIndex: number, direction: 1 | -1): Promise<void> {
  const available = enabledIndexes.value
  if (available.length === 0) return
  const position = available.indexOf(currentIndex)
  const start = position >= 0 ? position : 0
  const nextPosition = (start + direction + available.length) % available.length
  const nextIndex = available[nextPosition]
  if (nextIndex !== undefined) await focusOption(nextIndex)
}

async function moveToBoundary(boundary: 'first' | 'last'): Promise<void> {
  const available = enabledIndexes.value
  const index = boundary === 'first' ? available[0] : available.at(-1)
  if (index !== undefined) await focusOption(index)
}
</script>

<template>
  <div
    class="ui-segmented-control inline-flex max-w-full items-stretch rounded-lg font-sans p-2"
    :class="[
      `ui-segmented-control--${size}`,
      `ui-segmented-control--${variant}`,
      variant === 'solid'
        ? 'w-max gap-xs border-0 bg-transparent p-0'
        : 'w-max gap-2xs bg-surface-sunken',
    ]"
    role="radiogroup"
    :aria-label="label"
  >
    <button
      v-for="(option, index) in options"
      :key="`${String(option.value)}-${index}`"
      :ref="(element) => element && (buttons[index] = element as HTMLButtonElement)"
      class="ui-segmented-control__option min-w-0 cursor-pointer rounded-md border-0 leading-tight transition-[color,background-color,box-shadow] duration-[120ms] focus-visible:outline-[var(--ui-ring-width)] focus-visible:outline-offset-2 focus-visible:outline-ring disabled:cursor-not-allowed disabled:opacity-50"
      :class="[
        size === 'xs' ? 'px-xs py-2xs text-[0.6875rem]' : '',
        size === 'sm' ? 'px-2.5 py-1.5 text-xs' : '',
        size === 'md' ? 'px-sm py-xs text-[0.8125rem]' : '',
        size === 'lg' || size === 'xl' ? 'px-md py-2.5 text-sm' : '',

        // `filled` holds a common segment width so a short label cannot narrow its segment.
        variant === 'filled' ? 'min-w-19' : '',

        // One exclusive set per variant × selection: two competing colour or font-weight
        // utilities on the same element would be resolved by the emitted sheet's order rather
        // than by the order they are written here.
        !Object.is(option.value, model) && variant === 'solid'
          ? 'bg-surface-sunken text-fg font-semibold hover:bg-border hover:text-fg'
          : '',
        !Object.is(option.value, model) && variant === 'filled'
          ? 'bg-transparent text-muted font-light hover:bg-surface hover:text-primary'
          : '',
        !Object.is(option.value, model) && variant === 'default'
          ? 'bg-transparent text-muted font-semibold hover:bg-surface hover:text-primary'
          : '',
        Object.is(option.value, model) && variant === 'solid'
          ? 'ui-segmented-control__option--selected bg-primary text-primary-fg font-semibold shadow-none'
          : '',
        Object.is(option.value, model) && variant === 'filled'
          ? 'ui-segmented-control__option--selected bg-primary text-primary-fg font-normal shadow-none'
          : '',
        Object.is(option.value, model) && variant === 'default'
          ? 'ui-segmented-control__option--selected bg-surface text-primary font-semibold shadow-input'
          : '',
      ]"
      type="button"
      role="radio"
      :aria-checked="Object.is(option.value, model)"
      :aria-disabled="option.disabled === true"
      :disabled="option.disabled"
      :tabindex="tabIndex(index)"
      @click="select(option)"
      @keydown.right.prevent="move(index, 1)"
      @keydown.down.prevent="move(index, 1)"
      @keydown.left.prevent="move(index, -1)"
      @keydown.up.prevent="move(index, -1)"
      @keydown.home.prevent="moveToBoundary('first')"
      @keydown.end.prevent="moveToBoundary('last')"
    >
      <slot name="option" :option="option" :selected="Object.is(option.value, model)">
        {{ option.label }}
      </slot>
    </button>
  </div>
</template>
